import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import FormHelperText from '@mui/material/FormHelperText';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';

import CheckIcon from '@/assets/icons/check.svg?react';
import CloseIcon from '@/assets/icons/close.svg?react';
import PencilIcon from '@/assets/icons/pencil.svg?react';
import { useTranslator } from '@/hooks/useTranslator';
import { useUpdater } from '@/hooks/useUpdater';
import { EditComponentProps } from '@/types/editComponentProps';

interface Props {
  readonly label: string;
  readonly value: string;
  readonly displayValue?: string;
  readonly input: React.FunctionComponent<EditComponentProps>;

  onValidate?(value: string): Promise<string | null> | string | null;
  onUpdate(value: string): Promise<void>;
}

export const EditableValue: React.FC<Props> = (props: Props): React.ReactElement => {
  const { label, displayValue, value, input: Input, onUpdate, onValidate } = props;

  const [editing, setEditing] = React.useState<boolean>(false);
  const [executeUpdate, updateStatus] = useUpdater(onUpdate, value);
  const [error, setError] = React.useState<string | null>(null);

  const tr = useTranslator();

  const onEditStart = React.useCallback((): void => {
    setEditing(true);
  }, []);

  const onEditAbort = React.useCallback((): void => {
    setEditing(false);
  }, []);

  const onSubmit = React.useCallback(
    async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
      const { target: form } = event;

      event.stopPropagation();
      event.preventDefault();

      if (form instanceof HTMLFormElement) {
        const { elements } = form;

        const input = elements[0];
        if (input instanceof HTMLInputElement) {
          // Reset old error if any
          setError(null);
          // Validate again
          const error = await onValidate?.(input.value);
          if (!error) {
            // If no error thrown, we're good
            executeUpdate(input.value);
          } else {
            setError(error);
          }
        }
      }
    },
    [executeUpdate, onValidate],
  );

  React.useEffect((): void => {
    switch (updateStatus) {
      case QueryStatus.uninitialized:
        break;
      case QueryStatus.pending:
        break;
      case QueryStatus.fulfilled:
        setEditing(false);
        break;
      case QueryStatus.rejected:
        break;
    }
  }, [updateStatus]);

  const computedDisplayValue = React.useMemo((): string => {
    return tr(displayValue ?? label);
  }, [label, tr, displayValue]);

  if (editing) {
    return (
      <Box component="form" display="flex" alignItems="center" my={2} onSubmit={onSubmit}>
        <Box flex={1} mr={2}>
          <Input key={value} label={label} defaultValue={value} fullWidth={true} />
          {error && <FormHelperText error={true}>{tr(error)}</FormHelperText>}
        </Box>
        {updateStatus === QueryStatus.pending ? (
          <Box display="flex" alignItems="center" justifyContent="center" width={40} height={40}>
            <CircularProgress size={18} thickness={6} color="inherit" />
          </Box>
        ) : (
          <IconButton sx={styles.iconButton} type="submit">
            <CheckIcon fill="currentColor" />
          </IconButton>
        )}
        <IconButton
          sx={styles.iconButton}
          disabled={updateStatus === QueryStatus.pending}
          onClick={onEditAbort}>
          <CloseIcon fill="currentColor" />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="row" alignItems="center" gap="2px" height={24}>
      <Typography variant="body2" color="text.primary">
        {computedDisplayValue}
      </Typography>
      <IconButton sx={styles.iconButton} size="small" onClick={onEditStart}>
        <PencilIcon fill="currentColor" />
      </IconButton>
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  iconButton: {
    color: 'text.primary',
  },
};
